import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { ymdToDmy } from './reportDateParams'

/** Angular `ReportsTypeComponent.aireporturl` — CRM cash flow export. */
export function buildAiReportExportUrl(): string | null {
  const token = getAuthTokenFromStorage()
  if (!token) return null
  const base = getBrowserApiBaseUrl()
  return `${base}/api/export/crm_cash_flow?access_token=${encodeURIComponent(token)}`
}

/** Append `key[0]=`, `key[1]=` … like Angular export URLs. */
export function appendIndexedArrayParams(
  params: URLSearchParams,
  key: string,
  values: string[],
): void {
  values.forEach((value, index) => {
    params.set(`${key}[${index}]`, value)
  })
}

export function buildReportExportUrl(
  apiPath: string,
  queryParams: URLSearchParams,
): string | null {
  const token = getAuthTokenFromStorage()
  if (!token) return null
  const base = getBrowserApiBaseUrl()
  const params = new URLSearchParams(queryParams)
  params.set('access_token', token)
  params.set('view', 'xlsx')
  const path = apiPath.startsWith('/') ? apiPath : `/${apiPath}`
  return `${base}${path}?${params.toString()}`
}

/** Weekly reimbursements export uses DD-MM-YYYY dates. */
export function buildWeeklyReimbursementsExportParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  fromDt: string
  toDt: string
  year: string
  week_filter: string[]
}): URLSearchParams {
  const params = new URLSearchParams()
  params.set('page', String(p.page))
  params.set('perPage', String(p.perPage))
  params.set('sortBy', p.sortBy)
  params.set('orderBy', p.orderBy)
  if (p.year) params.set('year', p.year)
  const from = p.fromDt ? ymdToDmy(p.fromDt) : ''
  const to = p.toDt ? ymdToDmy(p.toDt) : ''
  if (from) params.set('fromDt', from)
  if (to) params.set('toDt', to)
  if (p.week_filter.length) appendIndexedArrayParams(params, 'week_filter', p.week_filter)
  return params
}

/** MRG report export — DD-MM-YYYY dates; `rentalStatus[n]` indexed arrays. */
export function buildMrgReportExportParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  q: string
  company_id: string
  property_id: string
  fromDt: string
  toDt: string
  claimfromDt: string
  claimtoDt: string
  rentalStatus: string[]
}): URLSearchParams {
  const params = new URLSearchParams()
  params.set('page', String(p.page))
  params.set('perPage', String(p.perPage))
  params.set('sortBy', p.sortBy)
  params.set('orderBy', p.orderBy)
  if (p.q.trim()) params.set('q', p.q.trim())
  if (p.company_id) params.set('company_id', p.company_id)
  if (p.property_id) params.set('property_id', p.property_id)
  const from = p.fromDt ? ymdToDmy(p.fromDt) : ''
  const to = p.toDt ? ymdToDmy(p.toDt) : ''
  if (from) params.set('fromDt', from)
  if (to) params.set('toDt', to)
  const claimFrom = p.claimfromDt ? ymdToDmy(p.claimfromDt) : ''
  const claimTo = p.claimtoDt ? ymdToDmy(p.claimtoDt) : ''
  if (claimFrom) params.set('claimfromDt', claimFrom)
  if (claimTo) params.set('claimtoDt', claimTo)
  if (p.rentalStatus.length) appendIndexedArrayParams(params, 'rentalStatus', p.rentalStatus)
  return params
}

/** PMC performance export — dates as YYYY-MM-DD. */
export function buildPmcPerformanceExportParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  fromDt: string
  toDt: string
  company_id: string
  sales_team: string[]
  report_type: 'red' | 'green'
}): URLSearchParams {
  const params = new URLSearchParams()
  params.set('page', String(p.page))
  params.set('perPage', String(p.perPage))
  params.set('sortBy', p.sortBy)
  params.set('orderBy', p.orderBy)
  params.set('report_type', p.report_type)
  if (p.fromDt) params.set('fromDt', p.fromDt)
  if (p.toDt) params.set('toDt', p.toDt)
  if (p.company_id) params.set('company_id', p.company_id)
  if (p.sales_team.length) appendIndexedArrayParams(params, 'sales_team', p.sales_team)
  return params
}
